import { z } from 'zod';
import { ManualMemoryEntryPointSchema } from './memory-entry-point.schema';

export const MessageRoleSchema = z.enum(['USER', 'ASSISTANT', 'TOOL', 'OTHER']);
export type MessageRole = z.infer<typeof MessageRoleSchema>;

export const ConversationMessageSchema = z.object({
  content: z.string(),
  role: MessageRoleSchema,
});
export type ConversationMessage = z.infer<typeof ConversationMessageSchema>;

export const MemoryCreationMethodSchema = z.enum([
  'manual',
  'automatic',
  'system',
]);
export type MemoryCreationMethod = z.infer<typeof MemoryCreationMethodSchema>;

export const MemoryStatusSchema = z.enum(['active', 'pending', 'rejected']);
export type MemoryStatus = z.infer<typeof MemoryStatusSchema>;

export const MemoryRecordSchema = z.object({
  recordId: z.string(),
  namespace: z.string(),
  content: z.string(),
  score: z.number().optional(),
  createdAt: z.string().optional(),
});
export type MemoryRecord = z.infer<typeof MemoryRecordSchema>;

export const MemoryStrategyNameSchema = z.enum([
  'wide_app',
  'user_preferences',
  'endpoint_facts',
  'business_context_app',
]);
export type MemoryStrategyName = z.infer<typeof MemoryStrategyNameSchema>;

export const MemoryRecordResponseSchema = z.object({
  recordId: z.string(),
  content: z.string(),
  strategy: MemoryStrategyNameSchema.optional(),
  score: z.number().optional(),
  createdAt: z.string().optional(),
  creationMethod: MemoryCreationMethodSchema.optional(),
  status: MemoryStatusSchema.optional(),
  rejectionReason: z.string().optional(),
});
export type MemoryRecordResponse = z.infer<typeof MemoryRecordResponseSchema>;

export const MemoryEventSchema = z.object({
  eventId: z.string(),
  sessionId: z.string(),
  actorId: z.string(),
  timestamp: z.number(),
  messages: z.array(ConversationMessageSchema),
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type MemoryEvent = z.infer<typeof MemoryEventSchema>;

export const MemorySessionSchema = z.object({
  sessionId: z.string(),
  actorId: z.string(),
  createdAt: z.string(),
});
export type MemorySession = z.infer<typeof MemorySessionSchema>;

export const EntityTypeSchema = z.enum([
  'application',
  'endpoint',
  'finding',
  'signal',
  'company',
]);
export type EntityType = z.infer<typeof EntityTypeSchema>;

export const EntityReferenceSchema = z.object({
  type: z.union([EntityTypeSchema, z.string()]),
  id: z.string(),
});
export type EntityReference = z.infer<typeof EntityReferenceSchema>;

export const CreateMemoryEventPayloadSchema = z.object({
  sessionId: z.string(),
  messages: z.array(ConversationMessageSchema),
  metadata: z
    .record(z.string(), z.object({ stringValue: z.string() }))
    .optional(),
  entityContext: z.array(EntityReferenceSchema),
});
export type CreateMemoryEventPayload = z.infer<
  typeof CreateMemoryEventPayloadSchema
>;

export const CreateMemoryRecordPayloadSchema = z.object({
  content: z.string().describe('The memory content to store'),
  entryPoint: ManualMemoryEntryPointSchema.optional().describe(
    'The surface that sent the write',
  ),
});
export type CreateMemoryRecordPayload = z.infer<
  typeof CreateMemoryRecordPayloadSchema
>;

export const ManualMemoryEntryPointQuerySchema = z.object({
  entryPoint: ManualMemoryEntryPointSchema.optional().describe(
    'The surface that sent the delete',
  ),
});
export type ManualMemoryEntryPointQuery = z.infer<
  typeof ManualMemoryEntryPointQuerySchema
>;

export const MarkMemoryRecordObsoleteBodySchema = z.object({
  note: z
    .string()
    .trim()
    .min(1)
    .describe('Why the memory is no longer true. Stored in full as evidence'),
  endpointId: z
    .string()
    .optional()
    .describe('Set when the memory is scoped to an endpoint'),
  entryPoint: ManualMemoryEntryPointSchema.optional().describe(
    'The surface that sent the request',
  ),
});
export type MarkMemoryRecordObsoleteBody = z.infer<
  typeof MarkMemoryRecordObsoleteBodySchema
>;
